import { type SharedData } from '@/types';
import { usePage } from '@inertiajs/react';
import { route as ziggyRoute, type RouteName } from 'ziggy-js';

/**
 * Hook that returns a locale-aware route() function.
 *
 * For positional params (string, number, array), Ziggy resolves {locale}
 * from its configured defaults — no manual injection needed.
 * For object params, locale is explicitly merged as a convenience.
 *
 * Usage:
 *   const route = useLocalizedRoute();
 *   route('dashboard')                                        // → /de/dashboard
 *   route('deposit-accounts.show', depositAccount.id)         // → /de/deposit-accounts/{id}
 *   route('deposit-accounts.show', [depositAccount.id])       // → /de/deposit-accounts/{id}
 *   route('deposit-accounts.show', { depositAccount: '123' }) // → /de/deposit-accounts/123
 */
export function useLocalizedRoute() {
    const { locale } = usePage<SharedData>().props;
    // During SSR, Ziggy config is only available via global.route (set up in ssr.tsx).
    // On the client, the imported ziggyRoute works directly.
    const routeFn = typeof window === 'undefined' ? route : ziggyRoute;
    const isSsr = typeof window === 'undefined';

    return function route<T extends RouteName>(
        name: T,
        params?: Record<string, unknown> | string | number | (string | number)[],
        absolute?: boolean,
    ): string {
        try {
            // Ziggy's defaults already provide `locale` for positional params,
            // so we must NOT inject it here — Ziggy excludes default params from
            // positional mapping, so prepending locale shifts all values to the wrong slots.
            if (typeof params === 'string' || typeof params === 'number') {
                return routeFn(name, params as never, absolute);
            }

            // Don't add `locale` here; Ziggy handles it for array params.
            if (Array.isArray(params)) {
                return routeFn(name, params as never, absolute);
            }

            // For object params, explicitly merge locale (don't override if already set)
            const mergedParams = { locale, ...(params ?? {}) };

            return routeFn(name, mergedParams as never, absolute);
        } catch (e) {
            // During SSR the Ziggy route map may be incomplete; return '#'.
            // In the browser, route generation can also fail when locale is
            // missing from props (e.g. on error pages before shared data is
            // fully resolved). Return '/' as a safe fallback so components
            // like Header don't crash and trigger the ErrorBoundary.
            if (isSsr) return '#';
            console.warn(`[useLocalizedRoute] Failed to generate route '${name}':`, e);
            return '/';
        }
    };
}
